import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Leaf,
  MapPin,
  Mail,
  Globe,
  Send,
  ArrowRight,
  PiggyBank,
  Sprout,
  BellRing,
  Smartphone,
  Heart,
  CheckCircle2,
  Instagram,
  ShoppingBag,
  Store,
  Weight,
  Cloud,
  Apple,
  Play,
  Quote,
  Menu,
  X,
  AlertCircle,
  Croissant,
} from "lucide-react";
import { translations } from "./i18n";
import { trackEvent, trackPageView } from "./lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { PiggyBank, Sprout, MapPin, BellRing, Smartphone, Heart, Croissant };

// Simple IntersectionObserver-based reveal
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const TikTokIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19.321 5.562a5.122 5.122 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.295h-3.214v12.8a2.56 2.56 0 1 1-2.56-2.56c.141 0 .28.012.415.035V8.955a5.84 5.84 0 0 0-.415-.015 5.87 5.87 0 1 0 5.87 5.87V8.33a8.32 8.32 0 0 0 4.855 1.554V6.67a5.099 5.099 0 0 1-.001-.001z" />
  </svg>
);

const Logo = ({ dark }) => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <img
      src="/brand/icon-80.png"
      alt="Bolsita Verde logo"
      width={40}
      height={40}
      className="object-contain shrink-0"
      style={{
        filter: dark ? "brightness(1.15)" : "none",
      }}
    />
    <span
      className="font-display text-[20px] tracking-tight"
      style={{ color: dark ? "#fff" : "var(--bv-ink)", fontWeight: 700 }}
    >
      Bolsita Verde
    </span>
  </div>
);

const Nav = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "features", label: t.nav.features },
    { id: "how", label: t.nav.how },
    { id: "partner", label: t.nav.partner },
    { id: "mission", label: t.nav.mission },
    { id: "app", label: t.nav.app },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{
        background: "rgba(248, 250, 248, 0.8)",
        borderBottom: "1px solid rgba(15,26,15,0.05)",
      }}
      data-testid="main-header"
    >
      <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <a href="#top" aria-label="Bolsita Verde - home">
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="text-sm font-semibold hover:text-[var(--bv-green)] transition-colors"
              style={{ color: "var(--bv-ink)" }}
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="lang-toggle"
            role="group"
            aria-label="Language"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => {
                setLang("es");
                trackEvent("lang_switch", { label: "es" });
              }}
              className={lang === "es" ? "active" : ""}
              data-testid="lang-es-btn"
            >
              ES
            </button>
            <button
              onClick={() => {
                setLang("en");
                trackEvent("lang_switch", { label: "en" });
              }}
              className={lang === "en" ? "active" : ""}
              data-testid="lang-en-btn"
            >
              EN
            </button>
          </div>
          <button
            className="lg:hidden p-2 rounded-lg"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(15,26,15,0.08)] bg-white">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="py-2.5 text-[15px] font-semibold"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const HeroVisual = ({ t }) => (
  <div className="relative">
    <div
      className="relative rounded-[28px] overflow-hidden shadow-xl"
      style={{ aspectRatio: "4/5", maxHeight: 580 }}
    >
      <img
        src="https://images.unsplash.com/photo-1706267701238-b4d69fc8f640?w=900&q=80"
        alt={t.hero.alt}
        className="w-full h-full object-cover"
        loading="eager"
      />
    </div>

    <div
      className="absolute -top-4 right-4 bg-white rounded-2xl px-5 py-3 shadow-lg floaty"
      style={{ animationDelay: "0.3s" }}
      data-testid="hero-badge-save"
    >
      <div
        className="font-num text-[24px] font-extrabold leading-none"
        style={{ color: "var(--bv-accent)" }}
      >
        {t.hero.badgeSave}
      </div>
      <div className="text-[11px] mt-1" style={{ color: "var(--bv-muted)" }}>
        {t.hero.badgeSaveSub}
      </div>
    </div>

    <div
      className="absolute -bottom-4 -left-2 bg-white rounded-2xl px-5 py-3 shadow-lg flex items-center gap-3 floaty"
      data-testid="hero-badge-waste"
    >
      <div
        className="w-10 h-10 rounded-xl grid place-items-center"
        style={{ background: "var(--bv-green-50)" }}
      >
        <Leaf size={18} color="var(--bv-green)" />
      </div>
      <div>
        <div
          className="font-num text-[24px] font-extrabold leading-none"
          style={{ color: "var(--bv-ink)" }}
        >
          {t.hero.badgeWaste}
        </div>
        <div className="text-[11px] mt-1" style={{ color: "var(--bv-muted)" }}>
          {t.hero.badgeWasteSub}
        </div>
      </div>
    </div>
  </div>
);

const Hero = ({ t }) => (
  <section
    id="top"
    className="relative overflow-hidden"
    style={{
      background:
        "linear-gradient(180deg, #f8faf8 0%, #eef7ee 60%, #ecf7e8 100%)",
    }}
  >
    <div
      className="blob"
      style={{
        width: 420,
        height: 420,
        background: "radial-gradient(circle, #c4ecab 0%, transparent 70%)",
        top: -120,
        right: -80,
      }}
    />
    <div
      className="blob"
      style={{
        width: 340,
        height: 340,
        background: "radial-gradient(circle, #dcf4c8 0%, transparent 70%)",
        bottom: -140,
        left: -60,
      }}
    />

    <div className="max-w-[1200px] mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center relative">
      <div className="slide-in-up">
        <span
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold mb-6"
          style={{
            background: "var(--bv-green-50)",
            color: "var(--bv-green)",
            border: "1px solid rgba(27, 94, 32, 0.18)",
          }}
          data-testid="hero-badge"
        >
          <MapPin size={14} /> {t.hero.badge}
        </span>

        <h1
          className="font-display leading-[1.0] tracking-tight mb-6"
          style={{
            fontSize: "clamp(44px, 6.5vw, 80px)",
            fontWeight: 900,
            color: "var(--bv-heading)",
          }}
          data-testid="hero-title"
        >
          {t.hero.title1}
          <br />
          <span className="hl-underline" style={{ color: "var(--bv-green)" }}>
            {t.hero.title2}
          </span>
        </h1>

        <p
          className="text-[17px] leading-relaxed max-w-[520px] mb-8"
          style={{ color: "var(--bv-muted)" }}
        >
          {t.hero.subtitle}
        </p>

        <div className="flex flex-wrap gap-3">
          <a
            href="#contact"
            className="btn-primary"
            data-testid="hero-cta-primary"
            onClick={() => trackEvent("hero_cta")}
          >
            <Send size={16} /> {t.hero.ctaPrimary}
          </a>
          <a
            href="#how"
            className="btn-ghost"
            data-testid="hero-cta-secondary"
          >
            {t.hero.ctaSecondary} <ArrowRight size={16} />
          </a>
        </div>
      </div>

      <HeroVisual t={t} />
    </div>
  </section>
);

const Marquee = ({ items }) => {
  const track = [...items, ...items, ...items];
  return (
    <div
      className="overflow-hidden py-5"
      style={{ background: "var(--bv-green)" }}
      data-testid="marquee-band"
    >
      <div className="flex gap-12 marquee-track whitespace-nowrap">
        {track.map((it, i) => (
          <div
            key={`${it}-${i}`}
            className="flex items-center gap-2.5 text-white text-[15px] font-semibold tracking-wide"
          >
            <CheckCircle2 size={18} color="#9fe36a" />
            {it}
          </div>
        ))}
      </div>
    </div>
  );
};

const Stats = ({ t }) => {
  const icons = [Weight, ShoppingBag, Store, Cloud];
  return (
    <section
      className="max-w-[1200px] mx-auto px-6 py-16"
      data-testid="stats-section"
    >
      <div className="text-center max-w-[640px] mx-auto mb-10">
        <div
          className="eyebrow mb-3 justify-center"
          style={{ display: "inline-flex" }}
        >
          {t.goals.eyebrow}
        </div>
        <h2
          className="font-display leading-[1.1] mb-3"
          style={{
            fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: 800,
            color: "var(--bv-heading)",
          }}
          data-testid="goals-title"
        >
          {t.goals.title}
        </h2>
        <p className="text-[16px]" style={{ color: "var(--bv-muted)" }}>
          {t.goals.subtitle}
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {t.goals.items.map((s, i) => {
          const Icon = icons[i];
          return (
            <div
              key={s.label}
              className="card text-center reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl mx-auto mb-4 grid place-items-center"
                style={{ background: "var(--bv-green-50)" }}
              >
                <Icon size={22} color="var(--bv-green)" />
              </div>
              <div className="stat-num">{s.value}</div>
              <div
                className="text-[13px] mt-2"
                style={{ color: "var(--bv-muted)" }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const Audience = ({ t }) => {
  const cards = [
    { data: t.audience.users, icon: ShoppingBag, color: "var(--bv-green)" },
    { data: t.audience.partners, icon: Store, color: "var(--bv-heading)" },
  ];
  return (
    <section
      id="audience"
      className="py-20 lg:py-24"
      style={{ background: "var(--bv-sand)" }}
      data-testid="audience-section"
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="max-w-[720px] mx-auto text-center mb-12">
          <div
            className="eyebrow mb-4 justify-center"
            style={{ display: "inline-flex" }}
          >
            {t.audience.eyebrow}
          </div>
          <h2
            className="font-display leading-[1.05] mb-4"
            style={{
              fontSize: "clamp(32px, 4.5vw, 52px)",
              fontWeight: 800,
              color: "var(--bv-heading)",
            }}
            data-testid="audience-title"
          >
            {t.audience.title}
          </h2>
          <p
            className="text-[16px] leading-relaxed"
            style={{ color: "var(--bv-muted)" }}
          >
            {t.audience.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {cards.map(({ data, icon: Icon, color }, i) => (
            <div
              key={data.label}
              className="card reveal p-8 flex flex-col"
              style={{ transitionDelay: `${i * 90}ms`, background: "#fff" }}
              data-testid={`audience-card-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-11 h-11 rounded-2xl grid place-items-center"
                  style={{ background: "var(--bv-green-50)", color }}
                >
                  <Icon size={22} />
                </div>
                <span
                  className="text-[12px] font-bold uppercase tracking-wider"
                  style={{ color }}
                >
                  {data.label}
                </span>
              </div>
              <h3
                className="font-display mb-5 leading-tight"
                style={{
                  fontSize: "clamp(22px, 2.2vw, 28px)",
                  fontWeight: 800,
                  color: "var(--bv-heading)",
                }}
              >
                {data.title}
              </h3>
              <ul className="space-y-3 mb-6 flex-1">
                {data.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <CheckCircle2
                      size={18}
                      className="mt-0.5 shrink-0"
                      color="var(--bv-green)"
                    />
                    <span
                      className="text-[15px] leading-relaxed"
                      style={{ color: "var(--bv-ink)" }}
                    >
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href={data.ctaHref}
                onClick={() =>
                  trackEvent(
                    i === 0 ? "audience_user_cta" : "audience_partner_cta",
                  )
                }
                className="inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-full font-bold text-[14px] transition-all hover:-translate-y-0.5"
                style={{
                  background:
                    i === 0 ? "var(--bv-green)" : "var(--bv-heading)",
                  color: "#fff",
                }}
                data-testid={`audience-cta-${i}`}
              >
                {data.cta} <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Features = ({ t }) => (
  <section
    id="features"
    className="max-w-[1200px] mx-auto px-6 py-20 lg:py-28"
    data-testid="features-section"
  >
    <div className="max-w-[720px] mb-14">
      <div className="eyebrow mb-4">{t.features.eyebrow}</div>
      <h2
        className="font-display leading-[1.05] mb-4"
        style={{
          fontSize: "clamp(36px, 5vw, 60px)",
          fontWeight: 800,
          color: "var(--bv-heading)",
        }}
      >
        {t.features.title}
      </h2>
      <p className="text-[17px]" style={{ color: "var(--bv-muted)" }}>
        {t.features.subtitle}
      </p>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {t.features.items.map((f, i) => {
        const Icon = ICONS[f.icon];
        return (
          <div
            key={f.title}
            className="card reveal"
            style={{ transitionDelay: `${i * 70}ms` }}
            data-testid={`feature-card-${i}`}
          >
            <div
              className="w-12 h-12 rounded-xl mb-5 grid place-items-center"
              style={{ background: "var(--bv-green-50)" }}
            >
              <Icon size={22} color="var(--bv-green)" />
            </div>
            <h3
              className="font-display text-[22px] font-bold mb-2"
              style={{ color: "var(--bv-heading)" }}
            >
              {f.title}
            </h3>
            <p
              className="text-[15px] leading-relaxed"
              style={{ color: "var(--bv-muted)" }}
            >
              {f.text}
            </p>
          </div>
        );
      })}
    </div>
  </section>
);

const HowItWorks = ({ t }) => (
  <section
    id="how"
    className="py-20 lg:py-28"
    style={{ background: "#fff" }}
    data-testid="how-section"
  >
    <div className="max-w-[1200px] mx-auto px-6">
      <div className="max-w-[720px] mb-16 text-center mx-auto">
        <div className="eyebrow mb-4 justify-center" style={{ display: "inline-flex" }}>
          {t.how.eyebrow}
        </div>
        <h2
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(36px, 5vw, 60px)",
            fontWeight: 800,
            color: "var(--bv-heading)",
          }}
        >
          {t.how.title}
        </h2>
      </div>

      <div className="relative">
        <div
          className="hidden md:block absolute top-10 left-[12%] right-[12%] h-[2px]"
          style={{
            background:
              "repeating-linear-gradient(90deg, var(--bv-green) 0 8px, transparent 8px 16px)",
            opacity: 0.4,
          }}
        />
        <div className="grid md:grid-cols-3 gap-8 relative">
          {t.how.steps.map((s, i) => (
            <div
              key={s.title}
              className="text-center reveal"
              style={{ transitionDelay: `${i * 120}ms` }}
              data-testid={`step-${i + 1}`}
            >
              <div
                className="w-20 h-20 rounded-full mx-auto mb-5 grid place-items-center text-white font-num text-[32px] font-extrabold"
                style={{
                  background:
                    "linear-gradient(135deg, #0e8a4c 0%, #0b6b3a 100%)",
                  boxShadow: "0 12px 28px -10px rgba(11,107,58,0.5)",
                }}
              >
                {i + 1}
              </div>
              <h3
                className="font-display text-[24px] font-bold mb-3"
                style={{ color: "var(--bv-heading)" }}
              >
                {s.title}
              </h3>
              <p
                className="text-[15px] leading-relaxed max-w-[320px] mx-auto"
                style={{ color: "var(--bv-muted)" }}
              >
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Partner = ({ t }) => (
  <section
    id="partner"
    className="relative py-20 lg:py-28 overflow-hidden"
    style={{ background: "var(--bv-green)" }}
    data-testid="partner-section"
  >
    <div className="grain" />
    <div
      className="blob"
      style={{
        width: 400,
        height: 400,
        background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
        top: -100,
        right: -100,
      }}
    />
    <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative">
      <div className="reveal">
        <div
          className="eyebrow mb-4"
          style={{ color: "#9fe36a" }}
        >
          <span style={{ color: "#9fe36a" }}>{t.partner.eyebrow}</span>
        </div>
        <h2
          className="font-display leading-[1.05] mb-5 text-white"
          style={{
            fontSize: "clamp(32px, 4.5vw, 54px)",
            fontWeight: 700,
          }}
        >
          {t.partner.title}
        </h2>
        <p className="text-[17px] text-white/85 mb-7 leading-relaxed">
          {t.partner.subtitle}
        </p>
        <ul className="space-y-3 mb-8">
          {t.partner.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-3 text-white/95 text-[15px]"
            >
              <CheckCircle2
                size={20}
                color="#9fe36a"
                className="mt-0.5 shrink-0"
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <a
          href="#contact"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold transition-all hover:-translate-y-0.5"
          style={{ background: "#fff", color: "var(--bv-green)" }}
          data-testid="partner-cta"
          onClick={() => trackEvent("partner_cta")}
        >
          {t.partner.cta} <ArrowRight size={16} />
        </a>
      </div>

      <div className="relative reveal">
        <div
          className="absolute top-4 left-4 z-10 bg-white rounded-full px-4 py-2 text-[12px] font-bold"
          style={{ color: "var(--bv-green)" }}
          data-testid="partner-badge"
        >
          {t.partner.badge}
        </div>
        <div
          className="rounded-[28px] overflow-hidden shadow-2xl"
          style={{ aspectRatio: "4/5", maxHeight: 560 }}
        >
          <img
            src="https://images.pexels.com/photos/7447275/pexels-photo-7447275.jpeg?auto=compress&cs=tinysrgb&w=900"
            alt={t.partner.alt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

const Mission = ({ t }) => (
  <section
    id="mission"
    className="py-20 lg:py-28"
    style={{ background: "var(--bv-sand)" }}
    data-testid="mission-section"
  >
    <div className="max-w-[960px] mx-auto px-6 text-center">
      <div
        className="eyebrow mb-4 justify-center"
        style={{ display: "inline-flex" }}
      >
        {t.mission.eyebrow}
      </div>
      <h2
        className="font-display leading-[1.05] mb-8"
        style={{
          fontSize: "clamp(36px, 5vw, 60px)",
          fontWeight: 800,
          color: "var(--bv-heading)",
        }}
      >
        {t.mission.title}
      </h2>

      <figure className="relative mb-10">
        <Quote
          size={44}
          className="mx-auto mb-4"
          color="var(--bv-green)"
          strokeWidth={1.5}
        />
        <blockquote
          className="font-display italic text-[24px] md:text-[30px] leading-snug max-w-[780px] mx-auto"
          style={{ color: "var(--bv-green)", fontWeight: 700 }}
        >
          "{t.mission.quote}"
        </blockquote>
      </figure>

      <p
        className="text-[16px] leading-relaxed mb-4 max-w-[700px] mx-auto"
        style={{ color: "var(--bv-muted)" }}
      >
        {t.mission.body1}
      </p>
      <p
        className="text-[16px] leading-relaxed max-w-[700px] mx-auto"
        style={{ color: "var(--bv-muted)" }}
      >
        {t.mission.body2}
      </p>
    </div>
  </section>
);

const Community = ({ t }) => (
  <section
    id="community"
    className="relative py-20 lg:py-24 overflow-hidden"
    style={{ background: "var(--bv-green)" }}
    data-testid="community-section"
  >
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 20% 30%, #fff 0%, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0%, transparent 45%)",
      }}
    />
    <div className="relative max-w-[880px] mx-auto px-6 text-center">
      <div
        className="eyebrow mb-5 justify-center"
        style={{ display: "inline-flex", color: "#fff", opacity: 0.85 }}
      >
        <Heart size={14} /> {t.community.eyebrow}
      </div>
      <h2
        className="font-display leading-[1.1] mb-6"
        style={{
          fontSize: "clamp(32px, 4.5vw, 52px)",
          fontWeight: 800,
          color: "#fff",
        }}
        data-testid="community-title"
      >
        {t.community.title}
      </h2>
      <p
        className="text-[17px] md:text-[18px] leading-relaxed mb-6 max-w-[720px] mx-auto"
        style={{ color: "rgba(255,255,255,0.92)" }}
      >
        {t.community.body}
      </p>
      <p
        className="font-display italic text-[20px] md:text-[24px] leading-snug mb-8 max-w-[720px] mx-auto"
        style={{ color: "#fff", fontWeight: 600 }}
      >
        "{t.community.highlight}"
      </p>
      <a
        href="#contact"
        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold transition-all hover:-translate-y-0.5"
        style={{ background: "#fff", color: "var(--bv-green)" }}
        data-testid="community-cta"
        onClick={() => trackEvent("community_cta")}
      >
        <Heart size={16} fill="currentColor" /> {t.community.cta}
      </a>
    </div>
  </section>
);

const StoreButtons = ({ t }) => (
  <div className="flex flex-wrap gap-3">
    <a
      href="https://apps.apple.com/cr/app/bolsita-verde/id6782341278?l=en-GB"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("ios_click", { label: "app_section" })}
      className="relative flex items-center gap-3 px-5 py-3.5 rounded-2xl border-2 transition-all hover:-translate-y-0.5"
      style={{
        borderColor: "var(--bv-ink)",
        background: "var(--bv-ink)",
        color: "#fff",
      }}
      data-testid="app-ios-btn"
    >
      <Apple size={28} />
      <div className="text-left leading-tight">
        <div className="text-[11px] opacity-75">Download on the</div>
        <div className="text-[16px] font-bold">{t.app.ios}</div>
      </div>
    </a>
    <a
      href="https://play.google.com/store/apps/details?id=app.bolsitaverde.productionready385cb552b2&pcampaignid=web_share"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("android_click", { label: "app_section" })}
      className="relative flex items-center gap-3 px-5 py-3.5 rounded-2xl border-2 transition-all hover:-translate-y-0.5"
      style={{
        borderColor: "var(--bv-ink)",
        background: "var(--bv-ink)",
        color: "#fff",
      }}
      data-testid="app-android-btn"
    >
      <Play size={26} fill="currentColor" />
      <div className="text-left leading-tight">
        <div className="text-[11px] opacity-75">Get it on</div>
        <div className="text-[16px] font-bold">{t.app.android}</div>
      </div>
    </a>
  </div>
);

const PhoneMockup = ({ t }) => (
  <div className="relative flex justify-center reveal">
    <div
      className="absolute -inset-10"
      style={{
        background:
          "radial-gradient(circle at center, rgba(118,210,59,0.18) 0%, transparent 65%)",
      }}
    />
    <div className="relative flex items-end gap-5">
      <div className="phone-frame floaty" style={{ animationDelay: "0.4s" }}>
        <div className="phone-screen flex flex-col items-center justify-between py-6 px-4">
          <img
            src="/brand/icon-80.png"
            alt="Bolsita Verde app icon"
            width={56}
            height={56}
            className="object-contain"
          />
          <div className="text-center">
            <div
              className="font-display text-[18px] font-bold"
              style={{ color: "var(--bv-green)" }}
            >
              Bolsita Verde
            </div>
            <div className="text-[11px] mt-1" style={{ color: "var(--bv-muted)" }}>
              iOS · Android
            </div>
          </div>
          <div className="flex gap-2 w-full">
            {[ShoppingBag, MapPin, Heart].map((Icon) => (
              <div
                key={Icon.displayName || Icon.name}
                className="flex-1 h-14 rounded-xl grid place-items-center"
                style={{ background: "var(--bv-green-50)" }}
              >
                <Icon size={20} color="var(--bv-green)" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AppDownload = ({ t }) => (
  <section
    id="app"
    className="relative py-20 lg:py-28 overflow-hidden"
    style={{ background: "#fff" }}
    data-testid="app-section"
  >
    <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
      <div className="reveal">
        <div className="eyebrow mb-4">{t.app.eyebrow}</div>
        <h2
          className="font-display leading-[1.05] mb-5"
          style={{
            fontSize: "clamp(36px, 5vw, 60px)",
            fontWeight: 800,
            color: "var(--bv-heading)",
          }}
        >
          {t.app.title}
        </h2>
        <p
          className="text-[17px] leading-relaxed max-w-[520px] mb-8"
          style={{ color: "var(--bv-muted)" }}
        >
          {t.app.subtitle}
        </p>

        <StoreButtons t={t} />
      </div>

      <PhoneMockup t={t} />
    </div>
  </section>
);

const ContactInfo = ({ t }) => (
  <div>
    <div className="eyebrow mb-4">{t.contact.eyebrow}</div>
    <h2
      className="font-display leading-[1.05] mb-5"
      style={{
        fontSize: "clamp(36px, 5vw, 60px)",
        fontWeight: 800,
        color: "var(--bv-heading)",
      }}
    >
      {t.contact.title}
    </h2>
    <p
      className="text-[17px] leading-relaxed mb-8 max-w-[460px]"
      style={{ color: "var(--bv-muted)" }}
    >
      {t.contact.subtitle}
    </p>

    <div className="space-y-5">
      <ContactItem
        icon={<Mail size={18} color="var(--bv-green)" />}
        label={t.contact.email}
        value={
          <a
            href="mailto:hola@tu-bolsitaverdecr.com"
            className="hover:underline"
            style={{ color: "var(--bv-ink)" }}
            data-testid="contact-email-link"
          >
            hola@tu-bolsitaverdecr.com
          </a>
        }
      />
      <ContactItem
        icon={<MapPin size={18} color="var(--bv-green)" />}
        label={t.contact.location}
        value={t.contact.locationValue}
      />
      <ContactItem
        icon={<Globe size={18} color="var(--bv-green)" />}
        label={t.contact.web}
        value={t.contact.webValue}
      />
    </div>
  </div>
);

const ContactSuccess = ({ t, lang, onReset }) => (
  <div className="text-center py-12" data-testid="contact-success">
    <div
      className="w-16 h-16 rounded-full mx-auto mb-5 grid place-items-center"
      style={{ background: "var(--bv-green-50)" }}
    >
      <CheckCircle2 size={30} color="var(--bv-green)" />
    </div>
    <h3
      className="font-display text-[26px] font-bold mb-2"
      style={{ color: "var(--bv-heading)" }}
    >
      {t.contact.form.successTitle}
    </h3>
    <p style={{ color: "var(--bv-muted)" }}>{t.contact.form.successText}</p>
    <button
      type="button"
      onClick={onReset}
      className="mt-6 text-[14px] font-bold"
      style={{ color: "var(--bv-green)" }}
      data-testid="contact-send-another-btn"
    >
      ↺ {lang === "es" ? "Enviar otro mensaje" : "Send another message"}
    </button>
  </div>
);

const ContactFields = ({ t, form, setForm }) => (
  <>
    <div className="grid md:grid-cols-2 gap-4 mb-4">
      <Field label={t.contact.form.name}>
        <input
          type="text"
          required
          minLength={2}
          className="input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="contact-name-input"
        />
      </Field>
      <Field label={t.contact.form.email}>
        <input
          type="email"
          required
          className="input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="contact-email-input"
        />
      </Field>
    </div>

    <Field label={t.contact.form.type}>
      <select
        className="select"
        value={form.inquiry_type}
        onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
        data-testid="contact-type-select"
      >
        {t.contact.form.types.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </Field>

    <div className="grid md:grid-cols-2 gap-4">
      <Field label={`${t.contact.form.restaurant} (${t.contact.form.optional})`}>
        <input
          type="text"
          className="input"
          value={form.restaurant}
          onChange={(e) => setForm({ ...form, restaurant: e.target.value })}
          data-testid="contact-restaurant-input"
        />
      </Field>
      <Field label={`${t.contact.form.whatsapp} (${t.contact.form.optional})`}>
        <input
          type="tel"
          className="input"
          maxLength={30}
          placeholder="+506 8888 8888"
          value={form.whatsapp}
          onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
          data-testid="contact-whatsapp-input"
        />
      </Field>
    </div>

    <Field label={t.contact.form.message}>
      <textarea
        required
        minLength={5}
        className="textarea"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        data-testid="contact-message-input"
      />
    </Field>
  </>
);

const ContactForm = ({ t, lang }) => {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [form, setForm] = useState({
    name: "",
    email: "",
    inquiry_type: t.contact.form.types[0],
    restaurant: "",
    whatsapp: "",
    message: "",
  });

  // Keep default inquiry type in sync with language
  useEffect(() => {
    setForm((f) => ({
      ...f,
      inquiry_type: t.contact.form.types[0],
    }));
  }, [t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        locale: lang,
      });
      trackEvent("contact_submit", { label: form.inquiry_type });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        inquiry_type: t.contact.form.types[0],
        restaurant: "",
        whatsapp: "",
        message: "",
      });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      className="py-20 lg:py-28"
      style={{ background: "var(--bv-sand)" }}
      data-testid="contact-section"
    >
      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
        <ContactInfo t={t} />

        <form
          onSubmit={onSubmit}
          className="card"
          style={{ padding: 32 }}
          data-testid="contact-form"
        >
          {status === "success" ? (
            <ContactSuccess t={t} lang={lang} onReset={() => setStatus("idle")} />
          ) : (
            <>
              <ContactFields t={t} form={form} setForm={setForm} />

              {status === "error" && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg mb-4"
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                  }}
                  data-testid="contact-error"
                >
                  <AlertCircle size={18} color="#dc2626" className="mt-0.5" />
                  <div className="text-[13px]" style={{ color: "#991b1b" }}>
                    <strong>{t.contact.form.errorTitle}.</strong>{" "}
                    {t.contact.form.errorText}
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-primary w-full justify-center mt-2"
                style={{
                  opacity: status === "sending" ? 0.7 : 1,
                }}
                data-testid="contact-submit-btn"
              >
                <Send size={16} />
                {status === "sending"
                  ? t.contact.form.sending
                  : t.contact.form.submit}
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block mb-4">
    <span
      className="block text-[13px] font-semibold mb-1.5"
      style={{ color: "var(--bv-ink)" }}
    >
      {label}
    </span>
    {children}
  </label>
);

const ContactItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div
      className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
      style={{ background: "var(--bv-green-50)" }}
    >
      {icon}
    </div>
    <div>
      <div
        className="text-[12px] font-semibold uppercase tracking-wider mb-0.5"
        style={{ color: "var(--bv-muted)" }}
      >
        {label}
      </div>
      <div
        className="text-[15px] font-semibold"
        style={{ color: "var(--bv-ink)" }}
      >
        {value}
      </div>
    </div>
  </div>
);

const Footer = ({ t, lang }) => (
  <footer
    className="relative pt-16 pb-8 text-white overflow-hidden"
    style={{ background: "#07432a" }}
    data-testid="main-footer"
  >
    <div className="grain" />
    <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-4 gap-10 relative">
      <div className="md:col-span-1">
        <Logo dark />
        <p className="text-white/70 mt-4 text-[14px] leading-relaxed max-w-[260px]">
          {t.footer.tagline}
        </p>
      </div>

      <div>
        <h4 className="text-[12px] font-bold uppercase tracking-wider mb-4 text-white/60">
          {t.footer.nav}
        </h4>
        <ul className="space-y-2.5">
          <FooterLink href="#features">{t.nav.features}</FooterLink>
          <FooterLink href="#how">{t.nav.how}</FooterLink>
          <FooterLink href="#partner">{t.nav.partner}</FooterLink>
          <FooterLink href="#mission">{t.nav.mission}</FooterLink>
          <FooterLink href="#contact">{t.nav.contact}</FooterLink>
        </ul>
      </div>

      <div>
        <h4 className="text-[12px] font-bold uppercase tracking-wider mb-4 text-white/60">
          {t.footer.app}
        </h4>
        <ul className="space-y-2.5 text-[14px] text-white/80">
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=app.bolsitaverde.productionready385cb552b2&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("android_click", { label: "footer" })}
              className="flex items-center gap-2 hover:text-white transition-colors"
              data-testid="footer-android-link"
            >
              <Play size={14} fill="currentColor" /> {t.app.android}
            </a>
          </li>
          <li>
            <a
              href="https://apps.apple.com/cr/app/bolsita-verde/id6782341278?l=en-GB"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("ios_click", { label: "footer" })}
              className="flex items-center gap-2 hover:text-white transition-colors"
              data-testid="footer-ios-link"
            >
              <Apple size={14} /> {t.app.ios}
            </a>
          </li>
        </ul>

        <h4 className="text-[12px] font-bold uppercase tracking-wider mt-6 mb-3 text-white/60">
          {t.footer.followUs}
        </h4>
        <div className="flex gap-2">
          <SocialBtn
            href="https://instagram.com/bolsitaverdecr"
            label="Instagram"
            testid="social-instagram"
            onClick={() => trackEvent("instagram_click")}
          >
            <Instagram size={16} />
          </SocialBtn>
          <SocialBtn href="#" label="TikTok" testid="social-tiktok">
            <TikTokIcon size={16} />
          </SocialBtn>
        </div>
      </div>

      <div>
        <h4 className="text-[12px] font-bold uppercase tracking-wider mb-4 text-white/60">
          {t.footer.legal}
        </h4>
        <ul className="space-y-2.5">
          <FooterLink href="#">{t.footer.privacy}</FooterLink>
          <FooterLink href="#">{t.footer.terms}</FooterLink>
        </ul>
        <div className="mt-6">
          <a
            href="mailto:hola@tu-bolsitaverdecr.com"
            className="text-[14px] text-white/80 hover:text-white flex items-center gap-2"
            data-testid="footer-email-link"
          >
            <Mail size={14} /> hola@tu-bolsitaverdecr.com
          </a>
        </div>
      </div>
    </div>

    <div className="max-w-[1200px] mx-auto px-6 mt-12 pt-6 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-3 relative">
      <div className="text-white/60 text-[13px]">
        © {new Date().getFullYear()} Bolsita Verde. {t.footer.rights}
      </div>
      <div className="text-white/50 text-[12px]">
        {lang === "es" ? "Hecho con" : "Made with"}{" "}
        <Heart size={12} className="inline -mt-0.5" fill="currentColor" /> en
        Costa Rica
      </div>
    </div>
  </footer>
);

const FooterLink = ({ href, children }) => (
  <li>
    <a
      href={href}
      className="text-[14px] text-white/80 hover:text-white transition-colors"
    >
      {children}
    </a>
  </li>
);

const SocialBtn = ({ href, label, children, testid, onClick }) => (
  <a
    href={href}
    aria-label={label}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className="w-9 h-9 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 transition-colors"
    data-testid={testid}
  >
    {children}
  </a>
);

export default function LandingPage() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("bv_lang") || "es";
    } catch {
      return "es";
    }
  });

  useEffect(() => {
    trackPageView(lang);
    // Only fire once per session on initial mount
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem("bv_lang", lang);
    } catch (error) {
      console.error("Failed to persist language preference:", error);
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const t = translations[lang];

  useReveal();

  return (
    <div data-testid="landing-root">
      <Nav lang={lang} setLang={setLang} t={t} />
      <main>
        <Hero t={t} />
        <Marquee items={t.marquee} />
        <Audience t={t} />
        <Stats t={t} />
        <Features t={t} />
        <HowItWorks t={t} />
        <Partner t={t} />
        <Mission t={t} />
        <Community t={t} />
        <AppDownload t={t} />
        <ContactForm t={t} lang={lang} />
      </main>
      <Footer t={t} lang={lang} />
    </div>
  );
}
